import { trigger, state, style, transition, animate } from "@angular/animations";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, ViewEncapsulation, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { catchError, debounceTime, distinctUntilChanged, EMPTY, map, of, Subject, switchMap, tap } from "rxjs";
import { ClonePlanogramModalComponent } from "src/app/modals/clone-planogram-modal/clone-planogram-modal.component";
import { CreateFolderModalComponent } from "src/app/modals/create-folder-modal/create-folder-modal.component";
import { FolderSelectionModalComponent } from "src/app/modals/folder-selection-modal/folder-selection-modal.component";
import { OpenLinkedPlanogramModalComponent } from "src/app/modals/open-linked-planogram-modal/open-linked-planogram-modal.component";
import { CreateReferencePlanogramModalComponent } from "src/app/modals/create-reference-planogram-modal/create-reference-planogram-modal.component";
import { CrossDatasourceWarningModalComponent } from "src/app/modals/cross-datasource-warning-modal/cross-datasource-warning-modal.component";
import { CapabilityType } from "src/app/models/capability-type";
import { Folder, FolderPathItem, FolderPlanogram, FolderPlanogramState, PlanogramSearchResult } from "src/app/models/folder";
import { OpenLinkedPlanogramChoice } from "src/app/models/reference-planogram";
import { SystemRole } from "src/app/models/roles";
import { DialogService } from "src/app/services/dialog.service";
import { FolderApiService } from "src/app/services/folder-api.service";
import { ToastService } from "src/app/services/toasts.service";
import { FormsModule } from "@angular/forms";
import { HasCapabilityDirective } from "../../directives/has-capability.directive";
import { NgIf, DatePipe } from "@angular/common";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { HomePageStore } from "./home-page.store";

interface PlanogramBadges {
  approved: boolean;
  inProgress: boolean;
  publishedDate?: Date;
}

interface PlanogramRowVm {
  planogram: FolderPlanogram;
  latestStateName: string;
  hasStates: boolean;
  isLinked: boolean;
  badges: PlanogramBadges;
  latestActivityTime: number;
  selected: boolean;
}

interface SearchResultRowVm {
  result: PlanogramSearchResult;
  latestStateName: string;
  badges: PlanogramBadges;
}

@Component({
    selector: 'app-home-page',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    // Scoped, not providedIn:'root' — state must be reset when the user
    // navigates away from the home page and back.
    providers: [HomePageStore],
    animations: [
        trigger('fadeInOut', [
            state('void', style({ opacity: 0 })),
            state('*', style({ opacity: 1 })),
            transition('void <=> *', animate('300ms ease-in-out')),
        ]),
    ],
    imports: [
        FormsModule,
        HasCapabilityDirective,
        NgIf,
        DatePipe,
        ScrollingModule,
    ],
})
export class HomeComponent implements OnInit {
  CapabilityType = CapabilityType;
  SystemRole = SystemRole;

  /** Precomputed view rows for displayedPlanograms — sorted, filtered by
   *  searchTerm. Built on data/search/selection change so the @for in the
   *  template never re-derives sort/badge/latest-state per CD cycle. */
  displayedPlanogramsVm: PlanogramRowVm[] = [];

  searchTerm: string = '';

  selectedPlanogramsToMove = new Map<string, FolderPlanogram>();

  // Global search
  globalSearchTerm: string = '';
  globalSearchResults: PlanogramSearchResult[] = [];
  searchResultsVm: SearchResultRowVm[] = [];
  isGlobalSearching: boolean = false;
  isGlobalSearchMode: boolean = false;
  private globalSearchSubject = new Subject<string>();

  private destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private dialogService: DialogService,
    private folderApiService: FolderApiService,
    private modalService: NgbModal,
    private store: HomePageStore,
    private cdr: ChangeDetectorRef,
  ) {}

  // Template bindings pass through to the store so the template stays unchanged.
  get rootFolders(): Folder[] { return this.store.rootFolders; }
  get displayedFolders(): Folder[] { return this.store.displayedFolders; }
  get displayedPlanograms(): FolderPlanogram[] { return this.store.displayedPlanograms; }
  get currentPath(): FolderPathItem[] { return this.store.currentPath; }
  get isLoading(): boolean { return this.store.isLoading; }
  get currentSelectedFolder(): FolderPathItem | null { return this.store.currentSelectedFolder; }

  ngOnInit(): void {
    // Root folders are loaded once; the sidebar uses them when on the root
    // view and the move/clone modals use them as picker context.
    this.store.loadRootFolders().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => this.cdr.markForCheck(),
      error: () => this.cdr.markForCheck(),
    });

    // React to folder navigation. switchMap cancels in-flight folder fetches
    // when the user clicks through folders fast; distinctUntilChanged avoids
    // re-fetch on identical re-emissions.
    this.route.paramMap.pipe(
      map(p => p.get('folderId') ?? null),
      distinctUntilChanged(),
      tap(folderId => {
        // Record the navigation target before the (async) folder-contents fetch
        // resolves, so a concurrent loadRootFolders() can't repaint the root
        // view over this pending folder navigation (the root-view flash).
        this.store.setTargetFolder(folderId);
        this.store.setLoading(true);
        this.cdr.markForCheck();
      }),
      switchMap(folderId => {
        if (!folderId) return of(null);
        return this.store.loadFolderContents(folderId).pipe(
          catchError(() => {
            this.toastService.show('Folder not found', { classname: 'bg-danger text-light' });
            this.router.navigate(['/']);
            return EMPTY;
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(folder => {
      if (folder) {
        this.store.applyFolderContents(folder);
        this.applyAfterFolderChange();
      } else {
        this.store.resetToRootView();
        this.applyAfterFolderChange();
      }
      this.cdr.markForCheck();
    });

    // Setup debounced global search
    this.globalSearchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
      tap(term => {
        if (!term || term.trim().length < 2) {
          this.isGlobalSearchMode = false;
          this.globalSearchResults = [];
          this.isGlobalSearching = false;
        } else {
          this.isGlobalSearching = true;
          this.isGlobalSearchMode = true;
        }
        this.cdr.markForCheck();
      }),
      switchMap(term => {
        if (!term || term.trim().length < 2) {
          return [];
        }
        return this.folderApiService.searchPlanogramsGlobal(term);
      })
    ).subscribe({
      next: (results) => {
        this.globalSearchResults = results;
        this.rebuildSearchResultsVm();
        this.isGlobalSearching = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isGlobalSearching = false;
        this.globalSearchResults = [];
        this.searchResultsVm = [];
        this.cdr.markForCheck();
      }
    });
  }

  /** Common view-side reset after the store's folder data has changed. */
  private applyAfterFolderChange(): void {
    this.searchTerm = '';
    this.clearPlanogramSelection();
    this.rebuildPlanogramVms();
  }

  // Stable identity for *cdkVirtualFor; closure is created once per component.
  trackByPlanogramGuid = (_index: number, vm: PlanogramRowVm) => vm.planogram.guid;

  /** Fixed row height (px) used by cdk-virtual-scroll-viewport. Must stay in
   *  sync with the `.planogram-row` min-height in the SCSS; mismatch causes
   *  rows to overlap or leave gaps. */
  readonly planogramRowHeight = 140;

  onSearchTermChange(term: string): void {
    this.searchTerm = term;
    this.rebuildPlanogramVms();
  }

  onGlobalSearchChange(term: string): void {
    this.globalSearchTerm = term;
    this.globalSearchSubject.next(term);
  }

  clearGlobalSearch(): void {
    this.globalSearchTerm = '';
    this.isGlobalSearchMode = false;
    this.globalSearchResults = [];
    this.searchResultsVm = [];
  }

  navigateToSearchResultFolder(result: PlanogramSearchResult): void {
    this.clearGlobalSearch();
    this.router.navigate(['/folder', result.folderId]);
  }

  openSearchResultPlanogram(result: PlanogramSearchResult): void {
    this.openPlanogram(result.planogram);
  }

  refreshCurrentView(): void {
    // Re-fetch both the root folder list (sidebar/move-picker context) and
    // the current folder's contents. Each is independent.
    this.store.loadRootFolders().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => this.cdr.markForCheck(),
      error: () => this.cdr.markForCheck(),
    });

    const folderId = this.route.snapshot.paramMap.get('folderId');
    if (!folderId) return;

    this.store.setLoading(true);
    this.store.loadFolderContents(folderId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (folder) => {
        this.store.applyFolderContents(folder);
        this.applyAfterFolderChange();
        this.cdr.markForCheck();
      },
      error: () => {
        this.store.setLoading(false);
        this.toastService.show('Folder not found', { classname: 'bg-danger text-light' });
        this.router.navigate(['/']);
      }
    });
  }

  private getLatestState(planogram: FolderPlanogram): FolderPlanogramState | null {
    if (!planogram.states || planogram.states.length === 0) return null;
    return planogram.states.reduce((latest, current) => {
      return new Date(current.dateCreated) > new Date(latest.dateCreated)
        ? current
        : latest;
    });
  }

  private buildBadges(planogram: FolderPlanogram): PlanogramBadges {
    if (!planogram.states || planogram.states.length === 0) {
      return { approved: false, inProgress: false };
    }
    const hasApproved = planogram.states.some(s => s.approved);
    const sortedStates = [...planogram.states].sort(
      (a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
    );
    const latestState = sortedStates[0];
    const approvedState = sortedStates.find(s => s.approved && s.publishedDate);

    return {
      approved: hasApproved,
      inProgress: !latestState.approved,
      publishedDate: approvedState?.publishedDate,
    };
  }

  private buildPlanogramRowVm(planogram: FolderPlanogram): PlanogramRowVm {
    const latestState = this.getLatestState(planogram);
    const latestStateTime = planogram.states?.length
      ? Math.max(...planogram.states.map(s => new Date(s.dateCreated).getTime()))
      : 0;
    const isLinked = !!planogram.referencePlanogramId;
    // Linked planograms have no states of their own, so their name comes from the reference (master).
    const name = isLinked
      ? (planogram.referenceName || 'Linked Planogram')
      : (latestState?.name || 'Unnamed Planogram');
    return {
      planogram,
      latestStateName: name,
      hasStates: !!(planogram.states && planogram.states.length > 0),
      isLinked,
      badges: this.buildBadges(planogram),
      latestActivityTime: Math.max(latestStateTime, new Date(planogram.dateCreated).getTime()),
      selected: this.selectedPlanogramsToMove.has(planogram.guid),
    };
  }

  private rebuildPlanogramVms(): void {
    const planograms = this.store.displayedPlanograms;
    if (!planograms) {
      this.displayedPlanogramsVm = [];
      return;
    }

    let source = planograms;
    const term = this.searchTerm?.toLowerCase().trim();
    if (term) {
      source = source.filter((p) => {
        const latestState = this.getLatestState(p);
        const nameMatch = latestState ? latestState.name.toLowerCase().includes(term) : false;
        // Linked rows display the reference's name (they have no states of their own), so match it too.
        const referenceNameMatch = p.referencePlanogramId
          ? (p.referenceName ?? '').toLowerCase().includes(term)
          : false;
        return nameMatch
          || referenceNameMatch
          || p.storeName.toLowerCase().includes(term)
          || p.merchName.toLowerCase().includes(term);
      });
    }

    this.displayedPlanogramsVm = source
      .map((p) => this.buildPlanogramRowVm(p))
      .sort((a, b) => b.latestActivityTime - a.latestActivityTime);
  }

  private rebuildSearchResultsVm(): void {
    this.searchResultsVm = (this.globalSearchResults ?? []).map((r) => ({
      result: r,
      latestStateName: this.getLatestState(r.planogram)?.name || 'Unnamed Planogram',
      badges: this.buildBadges(r.planogram),
    }));
  }

  selectFolder(folder: Folder): void {
    // Navigate to the folder route instead of managing internal state
    this.router.navigate(['/folder', folder.guid]);
  }

  navigateToBreadcrumb(index: number): void {
    this.clearPlanogramSelection();
    if (index < 0) {
      this.router.navigate(['/']);
    } else {
      const targetFolder = this.currentPath[index];
      this.router.navigate(['/folder', targetFolder.guid]);
    }
  }

  async deleteFolder(event: MouseEvent, folder: Folder): Promise<void> {
    event.stopPropagation();
    const confirmed = await this.dialogService.showConfirmation(
      'Delete Folder',
      `Are you sure you want to delete the folder "${folder.name}"? This cannot be undone.`
    );
    if (!confirmed) return;

    this.store.deleteFolder(folder).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        // If we were inside the folder we just deleted, go home
        if (this.route.snapshot.params['folderId'] === folder.guid) {
          this.router.navigate(['/']);
        }
        this.cdr.markForCheck();
      }
    });
  }

  async deletePlanogram(event: MouseEvent, planogram: FolderPlanogram): Promise<void> {
    event.stopPropagation();
    const displayName = planogram.referencePlanogramId
      ? (planogram.referenceName || 'Linked Planogram')
      : (this.getLatestState(planogram)?.name || 'Unnamed');
    const confirmed = await this.dialogService.showConfirmation(
      'Delete Planogram',
      `Are you sure you want to delete the planogram "${displayName}"? This cannot be undone.`
    );
    if (!confirmed) return;

    this.performDeletePlanogram(planogram, false);
  }

  private performDeletePlanogram(planogram: FolderPlanogram, confirm: boolean): void {
    this.store.deletePlanogram(planogram.guid, confirm).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: async result => {
        // Deleting a master (reference) with linked children needs explicit confirmation first.
        if (result.requiresConfirmation) {
          const proceed = await this.dialogService.showWarningConfirmation(
            'Delete reference planogram',
            `This planogram is referenced by ${result.linkedChildCount} linked planogram(s). Deleting it will convert each into an independent, standalone copy. Continue?`
          );
          if (proceed) this.performDeletePlanogram(planogram, true);
          return;
        }
        if (result.deleted) {
          this.selectedPlanogramsToMove.delete(planogram.guid);
          // If children were auto-unlinked, their cached rows are now stale (still flagged linked /
          // pointing at the deleted master), so re-fetch the folder. Otherwise just re-render.
          if (result.linkedChildCount > 0) {
            this.refreshCurrentView();
          } else {
            this.rebuildPlanogramVms();
          }
          this.cdr.markForCheck();
        }
      },
      error: () => this.cdr.markForCheck(),
    });
  }

  togglePlanogramSelection(vm: PlanogramRowVm, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    vm.selected = checked;
    if (checked) {
      this.selectedPlanogramsToMove.set(vm.planogram.guid, vm.planogram);
    } else {
      this.selectedPlanogramsToMove.delete(vm.planogram.guid);
    }
  }

  clearPlanogramSelection(): void {
    this.selectedPlanogramsToMove.clear();
    for (const vm of this.displayedPlanogramsVm) {
      vm.selected = false;
    }
  }

  get hasSelectedPlanograms(): boolean {
    return this.selectedPlanogramsToMove.size > 0;
  }

  openMovePlanogramsModal(): void {
    if (!this.hasSelectedPlanograms || !this.currentSelectedFolder) {
      this.toastService.show('No selection or folder context.', { classname: 'bg-warning text-dark', delay: 3000 });
      return;
    }

    const modalRef = this.modalService.open(FolderSelectionModalComponent, { size: 'lg', centered: true });
    modalRef.componentInstance.rootFolders = this.rootFolders;
    modalRef.componentInstance.initialPath = [...this.currentPath];

    modalRef.result.then((destinationFolderGuid: string) => {
      if (destinationFolderGuid) {
        const planogramGuids = Array.from(this.selectedPlanogramsToMove.keys());
        this.store.movePlanograms(destinationFolderGuid, planogramGuids).pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe({
          next: () => {
            this.clearPlanogramSelection();
            this.rebuildPlanogramVms();
            this.cdr.markForCheck();
          }
        });
      }
    }, () => {});
  }

  openBulkClonePage(): void {
    if (!this.hasSelectedPlanograms) {
      this.toastService.show('No planograms selected.', { classname: 'bg-warning text-dark', delay: 3000 });
      return;
    }

    // Bulk clone copies into a single target store, which belongs to exactly one data
    // source. A folder can list planograms from several data sources, so guard against a
    // mixed selection before navigating — otherwise the bulk-clone page would scope the
    // store search to just the first planogram's data source and silently clone the rest
    // against the wrong one. Show the user the split (grouped by data source) instead.
    const selected = Array.from(this.selectedPlanogramsToMove.values());
    const distinctDataSources = new Set(selected.map(p => p.dataSourceId ?? ''));
    if (distinctDataSources.size > 1) {
      const modalRef = this.modalService.open(CrossDatasourceWarningModalComponent, {
        size: 'lg',
        centered: true,
        scrollable: true,
      });
      modalRef.componentInstance.planograms = selected;
      modalRef.result.then(
        (chosenDataSourceId: string) => {
          // User chose to keep just one data source's planograms — reduce the selection and proceed.
          this.reduceSelectionToDataSource(chosenDataSourceId);
          if (this.hasSelectedPlanograms) this.navigateToBulkClone();
        },
        () => { /* dismissed — the user will adjust the selection manually */ }
      );
      return;
    }

    this.navigateToBulkClone();
  }

  /** Drop every selected planogram that doesn't belong to the given data source, and
   *  sync the row checkboxes to the reduced selection. */
  private reduceSelectionToDataSource(dataSourceId: string): void {
    for (const [guid, planogram] of this.selectedPlanogramsToMove) {
      if ((planogram.dataSourceId ?? '') !== dataSourceId) {
        this.selectedPlanogramsToMove.delete(guid);
      }
    }
    for (const vm of this.displayedPlanogramsVm) {
      vm.selected = this.selectedPlanogramsToMove.has(vm.planogram.guid);
    }
    this.cdr.markForCheck();
  }

  /** Hand the current selection to the bulk-clone page via router state (mirrors how the
   *  move flow passes folder context), then let that page collect store + folder. */
  private navigateToBulkClone(): void {
    this.router.navigate(['/bulk-clone'], {
      state: {
        planograms: Array.from(this.selectedPlanogramsToMove.values()),
        rootFolders: this.rootFolders,
        originFolderId: this.currentSelectedFolder?.guid ?? null,
      }
    });
  }

  canCreateFolder(): boolean {
    return this.currentPath.length > 0;
  }

  navigateToCreateFolder(): void {
    if (this.canCreateFolder() && this.currentSelectedFolder) {
      const modalRef = this.modalService.open(CreateFolderModalComponent);
      modalRef.componentInstance.parentFolder = this.currentSelectedFolder;
      modalRef.componentInstance.currentPath = this.currentPath;

      modalRef.result.then((result) => {
        if (result) {
          this.store.createFolderInCurrent(result.name).pipe(
            takeUntilDestroyed(this.destroyRef)
          ).subscribe({
            next: () => this.cdr.markForCheck(),
          });
        }
      }, () => {});
    }
  }

  navigateToCreatePlanogram(): void {
    const currentFolderId = this.currentSelectedFolder?.guid;
    this.router.navigate(['/create-planogram'], {
      queryParams: { folderId: currentFolderId }
    });
  }

  async openPlanogram(planogram: FolderPlanogram): Promise<void> {
    // Linked planogram: present the 3-option open modal instead of navigating directly.
    if (planogram.referencePlanogramId) {
      const modalRef = this.modalService.open(OpenLinkedPlanogramModalComponent, { centered: true });
      modalRef.componentInstance.planogram = planogram;
      modalRef.result.then((choice: OpenLinkedPlanogramChoice | null) => {
        if (choice) this.navigateForLinkedChoice(planogram, choice);
      }, () => {});
      return;
    }

    try {
      let stateIdToLoad: string | null = null;
      if (planogram.states && planogram.states.length > 0) {
        const sortedStates = [...planogram.states].sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
        stateIdToLoad = sortedStates[0].guid;
      }
      const route = stateIdToLoad ? `/planogram/${planogram.guid}/${stateIdToLoad}` : `/planogram/${planogram.guid}`;
      this.router.navigate([route]);
    } catch (e) {
      this.toastService.show('Planogram cannot be loaded right now.', { classname: 'bg-warning text-dark p-3 fs-5', delay: 3000 });
    }
  }

  /** Routes the 3 linked-planogram open choices.
   *  view-only  -> the linked planogram's own id (keeps the linked store identity; the backend
   *                resolver supplies the reference's layout and locks editing).
   *  reference-* -> the reference (master) planogram's id, fully editable, at the chosen state. */
  private navigateForLinkedChoice(planogram: FolderPlanogram, choice: OpenLinkedPlanogramChoice): void {
    const masterId = planogram.referencePlanogramId!;
    if (choice === 'view-only') {
      const stateId = planogram.referenceApprovedStateId ?? planogram.referenceLatestStateId;
      this.router.navigate([stateId ? `/planogram/${planogram.guid}/${stateId}` : `/planogram/${planogram.guid}`]);
    } else if (choice === 'reference-approved') {
      const stateId = planogram.referenceApprovedStateId;
      this.router.navigate([stateId ? `/planogram/${masterId}/${stateId}` : `/planogram/${masterId}`]);
    } else {
      const stateId = planogram.referenceLatestStateId;
      this.router.navigate([stateId ? `/planogram/${masterId}/${stateId}` : `/planogram/${masterId}`]);
    }
  }

  clonePlanogram(planogram: FolderPlanogram) {
    const modalRef = this.modalService.open(ClonePlanogramModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.planogram = planogram;
    modalRef.componentInstance.planogramFolderId = this.currentSelectedFolder?.guid;
    modalRef.componentInstance.rootFolders = this.rootFolders;
    modalRef.result.then((result: FolderPlanogram) => {
      if (result) this.router.navigate([`/planogram/${result.guid}/${result.states[0].guid}`]);
    }, () => {});
  }

  /** Opens the create-reference modal to link a master planogram into one or more stores. */
  createReference(planogram: FolderPlanogram) {
    const modalRef = this.modalService.open(CreateReferencePlanogramModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.planogram = planogram;
    modalRef.componentInstance.planogramFolderId = this.currentSelectedFolder?.guid;
    modalRef.componentInstance.rootFolders = this.rootFolders;
    modalRef.result.then((result) => {
      if (result) this.refreshCurrentView();
    }, () => {});
  }

  /** Converts a linked planogram into a standalone copy of the reference's current layout. */
  async unlinkPlanogram(planogram: FolderPlanogram): Promise<void> {
    const confirmed = await this.dialogService.showWarningConfirmation(
      'Unlink Planogram',
      "This will convert the linked planogram into an independent, editable copy of the reference planogram's current layout. Continue?"
    );
    if (!confirmed) return;

    this.store.unlinkPlanogram(planogram.guid).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (result) => {
        if (result) {
          this.toastService.show('Planogram unlinked successfully.', { classname: 'bg-success text-light', delay: 3000 });
          this.refreshCurrentView();
        }
      },
      error: () => this.toastService.show('Failed to unlink planogram.', { classname: 'bg-danger text-light', delay: 4000 }),
    });
  }
}
